import "./Template.css";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  ArrowBack,
  Close,
  Logout,
  NotificationsActive,
  NotificationsNone,
} from "@mui/icons-material";
import { Drawer } from "@mui/material";
import LangSwitch from "../LangSwitch/LangSwitch";
import { Menu as MenuIcon } from "react-feather";
import logoPNG from "../../../assets/leano/logo-c.png";
import logoTextWhite from "../../../assets/leano/name-b_w.png";
import routes from "../../../routes/routes";
import { useAuth } from "../../../auth/AuthContext";
import Button from "../Button/Button";
import { Form } from "../Form/Form";
import Navigation from "./Navigation/Navigation";
import CompanySwitch, {
  CompanySwitchBanner,
} from "./Navigation/CompanySwitch/CompanySwitch";
import { useModalDispatch } from "../Modal/ModalContext";
import Tabs from "../Tabs/Tabs";
import ImageContainer from "../ImageContainer/ImageContainer";
import { useTheme } from "../../../theme/ThemeContext";
import {
  NavigationProvider,
  useNavigationContext,
} from "./Navigation/NavigationContext";
import { getReseller } from "../../../utils";
import { useToastsDispatch } from "../Toasts/ToastsContext";
import Cookies from "universal-cookie";
import Typo from "../Typo/Typo";

// Combine side menu et main content into a single element
export default function Template({ children }) {
  return (
    <NavigationProvider>
      <Fragment>
        <Menu />
        {children}
      </Fragment>
    </NavigationProvider>
  );
}

function Menu() {
  const { logged, loadingLogged, activeCompany, loadingActiveCompany } =
    useAuth();

  const { elements, loading: loadingNavigation } = useNavigationContext();

  const [navDrawer, setNavDrawer] = useState(false);
  const toggleNavDrawer = () => setNavDrawer((navDrawer) => !navDrawer);

  return (
    <header className="Menu">
      <div className="Menu_wrapper">
        <div className="Menu-container">
          <MenuLogo />
          {!loadingLogged && logged && (
            <div className="MenuButtonsWrapper">
              <div
                style={{
                  visibility: navDrawer ? "hidden" : "visible",
                  display: "flex",
                  gap: "1rem",
                }}
              >
                {!loadingNavigation && elements && (
                  <div className="MenuButtonsContainer MenuQuickAccess">
                    {elements
                      .filter((element) => !element.hidden)
                      .map((element, index) => (
                        <Button
                          key={index}
                          icon={element.icon}
                          url={element.path}
                          title={element.title}
                        />
                      ))}
                  </div>
                )}
                <div className="MenuButtonsContainer">
                  {!loadingLogged &&
                    logged &&
                    !loadingActiveCompany &&
                    activeCompany && (
                      <Fragment>
                        <CompanySwitch />
                        <MenuNotifications />
                      </Fragment>
                    )}
                  <MenuLogout />
                </div>
              </div>

              <div>
                <MenuNavigation
                  navDrawer={navDrawer}
                  onNavigate={() => setNavDrawer(false)}
                  onToggle={toggleNavDrawer}
                />
              </div>
            </div>
          )}
          {!loadingLogged && !logged && <LangSwitch />}
        </div>
      </div>
      {!loadingLogged && logged && !loadingActiveCompany && activeCompany && (
        <div style={{ visibility: navDrawer ? "hidden" : "visible" }}>
          <CompanySwitchBanner />
        </div>
      )}
    </header>
  );
}

function MenuNavigation({ onNavigate, onToggle, navDrawer }) {
  return (
    <div className="MenuButton MenuNavigation">
      <Button icon={!navDrawer ? <MenuIcon /> : <Close />} onClick={onToggle} />

      <Drawer
        anchor={"top"}
        open={navDrawer}
        onClose={onToggle}
        className="NavigationDrawer"
      >
        <Navigation onNavigate={onNavigate} />
      </Drawer>
    </div>
  );
}

function MenuNotifications() {
  const { unreadNotifications, loadingNotifications } = useAuth();
  const { t } = useTranslation("notifications");

  const dispatchToast = useToastsDispatch();

  const unreadTrans = (
    <Trans
      t={t}
      i18nKey={`notifications:unread.${
        unreadNotifications > 1 ? "many" : "one"
      }`}
      values={
        unreadNotifications > 1 && {
          number: unreadNotifications,
        }
      }
    />
  );

  const cookies = new Cookies();

  const handleDispatch = () => {
    const cookieToastedNotification = cookies.get("toastedNotification", {
      path: "/",
    });

    if (!cookieToastedNotification && unreadNotifications) {
      dispatchToast({
        type: "add",
        variant: "info",
        heading: "",
        subheading: unreadTrans,
      });
      cookies.set("toastedNotification", true, { path: "/" });
    }
  };

  useEffect(() => {
    handleDispatch();
  }, []);

  if (loadingNotifications) return <></>;
  return (
    <div className="MenuButton MenuNotifications">
      <Button
        style={{
          color: unreadNotifications ? "var(--color-error-1)" : "unset",
        }}
        title={unreadNotifications > 0 ? unreadTrans : t("notifications:title")}
        icon={
          unreadNotifications ? <NotificationsActive /> : <NotificationsNone />
        }
        url={routes.Notifications.root}
      />
    </div>
  );
}

export function MenuLogo() {
  const { theme, loading } = useTheme();
  const { activeCompany, loadingActiveCompany, logged, loadingLogged } =
    useAuth();

  const linkProps = {
    to: routes.Home,
    className: "MenuLogoContainer",
  };

  if (loadingLogged) return <></>;
  if (!logged)
    return (
      <Link {...linkProps}>
        <LeanoLogo />
      </Link>
    );

  if (loading || loadingActiveCompany) return <></>;
  const resellerCompany = getReseller(activeCompany);
  return (
    <Link {...linkProps}>
      {theme && resellerCompany?.logo ? (
        <LogoContainer>
          <ImageContainer src={resellerCompany.logo} alt="logo" />
        </LogoContainer>
      ) : (
        <LeanoLogo />
      )}
    </Link>
  );
}

function LeanoLogo() {
  return (
    <LogoContainer>
      <ImageContainer src={logoPNG} alt="logo1" className="LeanoLogo1" />
      <ImageContainer src={logoTextWhite} alt="logo2" className="LeanoLogo2" />
    </LogoContainer>
  );
}

function LogoContainer({ children }) {
  return <div className="LogoContainer">{children}</div>;
}

function MenuLogout() {
  const { t } = useTranslation(["common", "account"]);
  const dispatchModal = useModalDispatch();

  return (
    <Button
      title={t("common:logout")}
      className="MenuLogout"
      icon={<Logout fontSize="small" />}
      onClick={() => {
        dispatchModal({
          type: "add",
          ...{
            title: t("account:logout.title"),
            message: t("account:logout.message"),
            component: <LogoutModal />,
          },
        });
      }}
    />
  );
}

function LogoutModal() {
  const { t } = useTranslation(["common", "account"]);
  const { onLogout } = useAuth();

  const handleLogout = async () => {
    const response = await onLogout();
    if (response.ok) {
      window.location.href = ""; // reload in order to clean up theme if any
    }
  };
  return <Form onSubmit={handleLogout} button={t("account:logout.button")} />;
}

// Main page element
export function Main({ children, className, themeSwitch, id }) {
  return (
    <main
      id={id}
      className={["Main", themeSwitch ? "uncenter" : "", className]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </main>
  );
}

export function Header({
  heading,
  onBackButtonClick,
  subheading,
  children,
  tabs = null,
  selectedID,
  onSelect,
  smallTabs = false,
  level = "h1",
  style,
}) {
  return (
    <header
      style={style}
      className={["Header", level].filter(Boolean).join(" ")}
    >
      {children ? (
        children
      ) : (
        <Fragment>
          {heading && (
            <Heading
              onBackButtonClick={onBackButtonClick}
              heading={heading}
              level={level}
            />
          )}
          {subheading && <SubHeading subheading={subheading} />}
          {tabs && (
            <div style={{ marginTop: "1rem", alignSelf: "stretch" }}>
              <Tabs
                tabs={tabs}
                selectedID={selectedID}
                onSelect={onSelect}
                smallTabs={smallTabs}
              />
            </div>
          )}
        </Fragment>
      )}
    </header>
  );
}

export function Heading({
  onBackButtonClick = null,
  heading,
  children,
  level = "h1",
}) {
  return (
    <div className="Header-heading">
      {onBackButtonClick && <BackButton onClick={onBackButtonClick} />}
      {level === "h1" ? (
        <Typo variant="h1">{children ? children : heading}</Typo>
      ) : (
        <Typo variant="h2">{children ? children : heading}</Typo>
      )}
    </div>
  );
}

export function SubHeading({ subheading, children }) {
  return (
    <Typo className="Header-subheading">
      {children ? children : subheading}
    </Typo>
  );
}

export function Content({ style, content, children, className }) {
  return (
    <div
      style={style}
      className={["Content", className].filter(Boolean).join(" ")}
    >
      {children ? children : content}
    </div>
  );
}

export function BackButton({ onClick, style, className }) {
  return (
    <span
      className={["BackButton", className].filter(Boolean).join(" ")}
      style={style}
      onClick={onClick}
    >
      <ArrowBack />
    </span>
  );
}
